import { useCallback, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useToast } from '@kinesis/bungle';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { useLogoutMutation, useSessionQuery } from '@/api/login';
import {
  ShareAccepted,
  useAcceptShareMutation,
  useDeclineShareMutation,
  useShareAcceptDetailsQuery,
} from '@/api/share';
import { loginFor } from '@/services/emailed-link';
import { sharedPathnameFor } from '@/services/launch';
import { ShareMessage } from './share-message';

// Home for a signed-in visitor: the root route routes a session through
// terms, orphan and launch.
const onHome = () => {
  window.location.assign('/');
};

// A claim lands on the thing that was shared. Diplomat names it by
// namespace and key; zoo maps that to a path. A namespace zoo has no path
// for is not guessed at — the claim still succeeded, so the confirmation
// stands in for the destination.
const openShared = (accepted: ShareAccepted) => {
  const pathname = sharedPathnameFor(accepted?.object);
  if (!pathname) {
    return false;
  }
  window.location.assign(pathname);
  return true;
};

// The share landing. Without a session the visitor goes to the log-in page
// for the link, where create-an-account is beside it; the token is held
// there and `launch` brings the finished session back here — after terms,
// after the orphan has made their organisation.
//
// With a finished session, one authenticated read on the token answers
// yours-or-not on the live rule: pending, and the share is shown with
// Accept and Decline — accepting is the recipient's explicit act, and
// lands on the shared thing; not yours, and they are told so.
const ShareAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const loggedIn = !!session?.accessToken;
  const {
    data: details,
    error: detailsError,
    isLoading: isDetailsLoading,
  } = useShareAcceptDetailsQuery(token, { skip: !loggedIn });
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const [accept, { isLoading: isAccepting }] = useAcceptShareMutation();
  const [decline, { isLoading: isDeclining }] = useDeclineShareMutation();
  const [claimed, setClaimed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const toast = useToast('globalTop');

  const onAccept = useCallback(async () => {
    try {
      const accepted = await accept(token).unwrap();
      setClaimed(true);
      if (!openShared(accepted)) {
        setClaimed(false);
        toast('We couldn’t open this share. Please try again.', {
          variant: 'error',
          duration: 6000,
        });
      }
    } catch (e) {
      toast('We couldn’t open this share. Please try again.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [accept, toast, token]);

  // Declining stays on the page: the face says what was declined, and
  // leaving is the recipient's next act. Home for a no-products identity
  // is the licensed-nothing page, which is no way to end a decline.
  const onDecline = useCallback(async () => {
    try {
      await decline(token).unwrap();
      setDeclined(true);
    } catch (e) {
      toast('Error declining share.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [decline, toast, token]);

  if (isSessionLoading || (loggedIn && isDetailsLoading)) {
    return <Loading />;
  }

  if (!loggedIn) {
    return <Navigate to={loginFor({ kind: 'share', token })} replace />;
  }

  const status = (detailsError as any)?.status;
  const code = (detailsError as any)?.data?.code;
  const notFound = status === 404;
  const notForThisAccount = code === 'share-not-for-this-account';
  const label = details?.label ?? 'this';
  const sharer = details?.sharerName;

  return (
    <Center>
      {declined && (
        <ShareMessage
          title='Share declined'
          actions={[{ label: 'Go to Kinesis', onClick: onHome }]}
        >
          You’ve declined <strong>{label}</strong>. Whoever shared it can share
          it again.
        </ShareMessage>
      )}
      {details?.state === 'pending' && !claimed && !declined && (
        <ShareMessage
          title='Shared with you'
          organisation={{
            name: details.ownerName ?? undefined,
            avatar: details.ownerAvatarUrl ?? undefined,
          }}
          actions={[
            {
              label: 'Accept',
              onClick: onAccept,
              loading: isAccepting,
            },
            {
              label: 'Decline',
              onClick: onDecline,
              variant: 'secondary',
              loading: isDeclining,
            },
          ]}
        >
          {sharer ? `${sharer} shared ` : 'Someone shared '}
          <strong>{label}</strong> with you.
        </ShareMessage>
      )}
      {claimed && <Loading />}
      {/* Details answers a spent share to its acceptor only, and accept is
          idempotent for them, so the emailed link keeps opening the
          disclosure after it has been accepted. */}
      {details?.state === 'spent' && !claimed && (
        <ShareMessage
          title='Already accepted'
          actions={[
            { label: 'Open', onClick: onAccept, loading: isAccepting },
            { label: 'Go to Kinesis', onClick: onHome, variant: 'secondary' },
          ]}
        >
          You’ve already accepted <strong>{label}</strong>. Open it to read it.
        </ShareMessage>
      )}
      {details?.state === 'expired' && (
        <ShareMessage title='Share expired'>
          {sharer ? `${sharer}’s share of ` : 'The share of '}
          <strong>{label}</strong> has expired. Ask {sharer ?? 'them'} to share
          it again.
        </ShareMessage>
      )}
      {notForThisAccount && (
        <ShareMessage
          title='Not for this account'
          actions={[
            {
              label: 'Log in with another account',
              onClick: () => logout({ to: loginFor({ kind: 'share', token }) }),
              loading: isLoggingOut,
            },
            { label: 'Go to Kinesis', onClick: onHome, variant: 'secondary' },
          ]}
        >
          This share was sent to a different email address. You’re signed in as{' '}
          <strong>{session?.email}</strong>. Ask whoever shared it to send it to
          this address, or log in with the address it was sent to.
        </ShareMessage>
      )}
      {notFound && (
        <ShareMessage title='Share not found'>
          This share isn’t available. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {!details && detailsError && !notFound && !notForThisAccount && (
        <ShareMessage title='Something went wrong'>
          We couldn’t open this share. Please try again.
        </ShareMessage>
      )}
    </Center>
  );
};

export { ShareAccept };
